import { Database, Kanban, Mail, TestTube2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LOCAL_SUPABASE_STUDIO_URL, LOCAL_INBUCKET_URL } from "@/config/local-supabase-ports";
import { useRole } from "@/hooks/useRole";
import { useNavigate } from "@tanstack/react-router";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";

// Only render in local development.
const isLocalDev =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

/**
 * DevToolbar
 *
 * Floating dev-only panel at the bottom-right of every page. Holds quick
 * links (Supabase Studio, Inbucket, Roadmap) and toggles for Claude Code
 * harness features (currently: the Playwright auto-verify Stop hook).
 *
 * Renders only on localhost / 127.0.0.1.
 */
export function DevToolbar() {
  const { isAdmin } = useRole();
  const navigate = useNavigate();

  const utils = trpc.useUtils();
  const autoVerify = trpc.dev.getAutoVerify.useQuery(undefined, {
    enabled: isLocalDev,
    staleTime: Infinity,
  });
  const setAutoVerify = trpc.dev.setAutoVerify.useMutation({
    onSuccess: () => {
      void utils.dev.getAutoVerify.invalidate();
    },
  });

  if (!isLocalDev) return null;

  const playwrightEnabled = autoVerify.data?.enabled ?? true;

  return (
    <div
      className="fixed right-4 bottom-4 z-50 flex flex-col items-stretch gap-1.5 rounded-lg border border-border/60 bg-background/95 p-2 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/80"
      aria-label="Dev tools"
    >
      <div className="px-1 text-[10px] font-semibold tracking-widest text-muted-foreground uppercase">
        Dev
      </div>
      <Button
        variant="outline"
        size="sm"
        className="justify-start border-green-500/50 text-green-500 hover:bg-green-500/10"
        onClick={() => window.open(LOCAL_SUPABASE_STUDIO_URL, "_blank")}
      >
        <Database className="mr-1.5 h-4 w-4" />
        Supabase
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="justify-start border-blue-500/50 text-blue-500 hover:bg-blue-500/10"
        onClick={() => window.open(LOCAL_INBUCKET_URL, "_blank")}
      >
        <Mail className="mr-1.5 h-4 w-4" />
        Inbox
      </Button>
      {isAdmin && (
        <Button
          variant="outline"
          size="sm"
          className="justify-start border-purple-500/50 text-purple-500 hover:bg-purple-500/10"
          onClick={() => navigate({ to: "/dashboard/roadmap" })}
        >
          <Kanban className="mr-1.5 h-4 w-4" />
          Roadmap
        </Button>
      )}
      <Button
        variant="outline"
        size="sm"
        disabled={setAutoVerify.isPending || autoVerify.isLoading}
        className={cn(
          "justify-start",
          playwrightEnabled
            ? "border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
            : "border-border text-muted-foreground line-through decoration-1 hover:bg-muted",
        )}
        onClick={() => setAutoVerify.mutate({ enabled: !playwrightEnabled })}
        title={
          playwrightEnabled
            ? "Auto-verify hook ON — click to disable for this project"
            : "Auto-verify hook OFF — click to re-enable"
        }
      >
        <TestTube2 className="mr-1.5 h-4 w-4" />
        Playwright
      </Button>
    </div>
  );
}
